import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import classNames from "classnames";
import TitleCover from "components/shared/TitleCover";
import type { TitleCoverColor } from "components/shared/TitleCover";
import type { IWorkTitleSummary } from "containers/Works/types";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import styles from "./style.module.scss";

export type SalesTitleSelection = number | "all" | "forfait";

type TProps = {
    titles: IWorkTitleSummary[];
    selectedTitleId: SalesTitleSelection;
    onSelect: (titleId: SalesTitleSelection) => void;
    showForfait?: boolean;
    showAll?: boolean;
    label?: string;
    workName?: string;
    workAvatarUrl?: string | null;
};

type SalesTitleChipProps = {
    selected: boolean;
    onClick: () => void;
    name: string;
    subtitle?: string;
    cover?: { url?: string; title: string; color: TitleCoverColor };
    icon?: ReactNode;
};

const SalesTitleChip = ({ selected, onClick, name, subtitle, cover, icon }: SalesTitleChipProps) => (
    <button
        type="button"
        role="radio"
        aria-checked={selected}
        className={classNames(styles["title-chip"], {
            [styles["title-chip--selected"]]: selected,
        })}
        onClick={onClick}
    >
        {icon ? (
            <span className={styles["title-chip-all-icon"]}>{icon}</span>
        ) : (
            cover && (
                <span className={styles["title-chip-cover"]}>
                    <TitleCover coverUrl={cover.url} title={cover.title} color={cover.color} />
                </span>
            )
        )}
        <span className={styles["title-chip-text"]}>
            <span className={styles["title-chip-name"]}>{name}</span>
            {subtitle ? <span className={styles["title-chip-ean"]}>{subtitle}</span> : null}
        </span>
    </button>
);

const SalesTitleSwitcher = ({
    titles,
    selectedTitleId,
    onSelect,
    showForfait = false,
    showAll = true,
    label,
    workName,
    workAvatarUrl,
}: TProps) => {
    const { t } = useTranslation();

    if (titles.length <= 1 && !showForfait) return null;

    const forfaitLabel = t("Management.SalesForfait");
    const pickerLabel = label ?? t("Management.SalesTitlePickerLabel");

    return (
        <div className={styles["title-switcher"]}>
            <span className={styles["title-switcher-label"]}>{pickerLabel}</span>
            <div className={styles["title-switcher-rail"]} role="radiogroup" aria-label={pickerLabel}>
                {showAll && (
                    <SalesTitleChip
                        selected={selectedTitleId === "all"}
                        onClick={() => onSelect("all")}
                        name={t("Management.SalesAllTitles")}
                        icon={<LayersOutlinedIcon sx={{ fontSize: 18 }} />}
                    />
                )}
                {showForfait && (
                    <SalesTitleChip
                        selected={selectedTitleId === "forfait"}
                        onClick={() => onSelect("forfait")}
                        name={workName ?? forfaitLabel}
                        subtitle={forfaitLabel}
                        cover={{
                            url: workAvatarUrl ?? undefined,
                            title: workName ?? forfaitLabel,
                            color: "work-color",
                        }}
                    />
                )}
                {titles.map((title) => (
                    <SalesTitleChip
                        key={title.id}
                        selected={typeof selectedTitleId === "number" && Number(selectedTitleId) === Number(title.id)}
                        onClick={() => onSelect(title.id)}
                        name={title.name}
                        subtitle={title.ean13}
                        cover={{
                            url: title.avatarUrl ?? undefined,
                            title: title.name,
                            color: "title-color",
                        }}
                    />
                ))}
            </div>
        </div>
    );
};

export default SalesTitleSwitcher;
